import { motion } from "motion/react";
import { Check } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

/**
 * Read-only funnel progress indicator — visual mirror of react-bits Stepper
 * (@react-bits/Stepper-JS-CSS), minus the wizard navigation. Each step
 * animates between inactive / active / complete states; the connector between
 * steps fills in as the funnel advances.
 *
 * Use for any passive "pipeline progress" display (batch phase, account phase,
 * onboarding status). For interactive multi-step flows, use Stepper from
 * components/Stepper.jsx directly.
 */
export function FunnelProgress({
  steps,
  currentStep,
  className,
}: {
  /** Ordered list of step labels. */
  steps: readonly string[];
  /** 1-based index of the active step. Use steps.length + 1 to mark "all complete". */
  currentStep: number;
  className?: string;
}): ReactNode {
  return (
    <div className={cn("flex w-full items-center", className)}>
      {steps.map((label, i) => {
        const stepNumber = i + 1;
        const status =
          currentStep === stepNumber
            ? "active"
            : currentStep > stepNumber
              ? "complete"
              : "inactive";
        const isLast = i === steps.length - 1;

        return (
          <div key={label} className="flex min-w-0 flex-1 items-center last:flex-none">
            <div className="flex min-w-0 flex-col items-center gap-1.5">
              <StepIndicator stepNumber={stepNumber} status={status} />
              <span
                className={cn(
                  "truncate text-[10px] font-medium transition-colors",
                  status === "active" && "text-accent",
                  status === "complete" && "text-foreground",
                  status === "inactive" && "text-muted-foreground/60"
                )}
              >
                {label}
              </span>
            </div>
            {!isLast && <StepConnector isComplete={currentStep > stepNumber} />}
          </div>
        );
      })}
    </div>
  );
}

function StepIndicator({
  stepNumber,
  status,
}: {
  stepNumber: number;
  status: "inactive" | "active" | "complete";
}): ReactNode {
  return (
    <motion.div
      animate={status}
      initial={false}
      variants={{
        inactive: {
          backgroundColor: "var(--muted)",
          borderColor: "var(--border)",
        },
        active: {
          backgroundColor: "color-mix(in oklch, var(--accent) 12%, transparent)",
          borderColor: "var(--accent)",
        },
        complete: {
          backgroundColor: "var(--accent)",
          borderColor: "var(--accent)",
        },
      }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border"
    >
      {status === "complete" ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-3 w-3 text-white"
        >
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : status === "active" ? (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="h-2 w-2 rounded-full bg-accent"
        />
      ) : (
        <span className="font-mono text-[10px] font-semibold text-muted-foreground">
          {stepNumber}
        </span>
      )}
    </motion.div>
  );
}

function StepConnector({ isComplete }: { isComplete: boolean }): ReactNode {
  return (
    <div className="relative mx-1.5 h-0.5 flex-1 overflow-hidden rounded-full bg-border">
      <motion.div
        initial={false}
        animate={{ width: isComplete ? "100%" : "0%" }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-y-0 left-0 bg-accent"
      />
    </div>
  );
}

/**
 * Standalone Check icon export so it's tree-shakeable if consumers only want
 * the icon without the full progress bar.
 */
export { Check as FunnelCheck };
